// @flow

import * as React from 'react';
import classNames from 'classnames';

import Badge from 'sail/Badge';
import Box from 'sail/Box';
import Group from 'sail/Group';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {Caption, BodyMono} from 'sail/Text';
import type {MarkdocTagSchema} from 'docs/markdoc';
import './MarkdocAttributeReference.scss';

type DefaultValueProps = {|
  defaultValue: React.Node,
|};

const DefaultValue = ({defaultValue}: DefaultValueProps): React.Node => {
  if (!defaultValue) {
    return <BodyMono color="disabled">none</BodyMono>;
  }

  return <BodyMono color="blue">{defaultValue}</BodyMono>;
};

type MarkdocAttributeReferenceProps = {|
  tag: MarkdocTagSchema,
|};

export const MarkdocAttributeReference = ({
  tag,
}: MarkdocAttributeReferenceProps) => {
  if (!tag.attributes || Object.keys(tag.attributes).length < 1) {
    return null;
  }

  const generateTypeString = (attr) => {
    // matches can be a function or an array
    if (
      attr.matches?.length &&
      typeof attr.matches === 'object' &&
      attr.matches.map
    ) {
      return `${attr.matches.map((a) => `"${a}"`).join(' | ')}`;
    } else {
      return attr.type || 'unknown';
    }
  };

  const rows = (Object.entries(tag.attributes): any).map(
    ([name, attr], index) => {
      let descriptionRow = null;
      if (attr.description) {
        descriptionRow = (
          <DataTableRow key={`${name}-description`}>
            <DataTableCell colSpan={3}>
              <Group spacing={4}>
                <Caption color="gray">{attr.description}</Caption>
              </Group>
            </DataTableCell>
          </DataTableRow>
        );
      }

      const attributeRow = (
        <DataTableRow
          key={name}
          className={classNames({
            'MarkdocAttributeReference-TableRow-hasDescription': !!descriptionRow,
          })}
        >
          <DataTableCell width={200} emphasized wrap={false}>
            <Box>
              <BodyMono>{name} </BodyMono>
              {attr.required && <Badge variant="tag" label="Required" />}
            </Box>
          </DataTableCell>
          <DataTableCell>{generateTypeString(attr)}</DataTableCell>
          <DataTableCell width={200}>
            <DefaultValue defaultValue={attr.default} />
          </DataTableCell>
        </DataTableRow>
      );

      return [attributeRow, descriptionRow];
    },
  );

  return (
    <DataTable className="MarkdocAttributeReference" size="small">
      <DataTableHead
        columns={[{label: 'Name'}, {label: 'Type'}, {label: 'Default value'}]}
      />
      <DataTableBody>{rows}</DataTableBody>
    </DataTable>
  );
};
